import { useEffect } from 'react';
import { useWebchatAgent } from '@/hooks/use-webchat-agent/use-webchat-agent';
import { useWebchatStore } from '@/store/webchat.store';
import { WebchatHeader } from './components/webchat-header/webchat-header';
import { SessionList } from './components/session-list/session-list';
import { ChatPanel } from './components/chat-panel/chat-panel';
import { ActionsSidebar } from './components/actions-sidebar/actions-sidebar';
import { LogsSidebar } from './components/logs-sidebar/logs-sidebar';
import { HistoryPanel } from './components/history-panel/history-panel';
import styles from './webchat.module.scss';
import clsx from 'clsx';

// Request microphone permission once when entering the Command Center so it's
// ready for AI-call claims and conference calls without a delay or popup.
const requestMicPermission = () => {
  navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((stream) => stream.getTracks().forEach((t) => t.stop()))
    .catch(() => {
      // User denied or browser blocked — claiming AI calls will show an error
    });
};

export const Webchat = () => {
  const { sendTyping, sendToggleTranslate, sendSetAgentLanguage } = useWebchatAgent();
  const activeTab = useWebchatStore((s) => s.activeTab);

  useEffect(() => {
    requestMicPermission();
  }, []);

  // Both panels stay mounted; we toggle visibility via inline `display`
  // instead of conditionally rendering one or the other. The previous
  // ternary remounted the entire panel subtree on every Live ↔ History
  // switch, which fired all the panels' initial React Query fetches +
  // Antd Tabs/Drawer/Table style injections at once — visible as a
  // global flash on tab click. Keeping both mounted means the switch
  // is a single CSS property update, no remount, no flash.
  return (
    <div className={clsx(styles.container, activeTab === 'live' && styles.liveContainer)}>
      <WebchatHeader onToggleTranslate={sendToggleTranslate} onSetAgentLanguage={sendSetAgentLanguage} />
      <div
        className={styles.panelContainer}
        style={{ display: activeTab === 'live' ? 'flex' : 'none' }}
      >
        <SessionList />
        <ChatPanel onTyping={sendTyping} />
        <ActionsSidebar onSetAgentLanguage={sendSetAgentLanguage} onToggleTranslate={sendToggleTranslate} />
        <LogsSidebar />
      </div>
      <div style={{ display: activeTab === 'history' ? 'contents' : 'none' }}>
        <HistoryPanel />
      </div>
    </div>
  );
};